<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import { computed, unref } from 'vue';

import { useGenerateIds } from '#public_ts/composables';

import type { CheckboxListValue, SelectType } from './radio-list';
import { isGroupHeader, isOptionItem, itemsToArray } from './radio-list';

const props = defineProps<{
    name?: string;
    modelValue?: CheckboxListValue;
    type?: SelectType;
    search?: string;
    withTitles?: boolean;
    withMargin?: boolean;
}>();
const emit = defineEmits<{
    (event: 'update:modelValue', value: CheckboxListValue): void;
}>();
const selectedItems = useVModel(props, 'modelValue', emit, { passive: true });

const generateIds = useGenerateIds();
const _name = props.name ?? generateIds();

const options = computed(() => itemsToArray(props.type, generateIds));
const filteredOptions = computed(() => {
    const _searchQuery = unref(props.search)?.trim()?.toLowerCase();

    if(!_searchQuery) {
        return unref(options);
    }

    return unref(options).filter((item) => {
        if(!isOptionItem(item)) {
            return true;
        }
        if(!item.text && (!item.title || item.title === '--')) {
            return true;
        }
        return item.title?.toLowerCase().includes(_searchQuery)
        || item.text?.toLowerCase().includes(_searchQuery);
    });
});

function isChecked(id: string) {
    const current = unref(selectedItems);
    if(current instanceof Map) {
        return current.get(id) ? true : undefined;
    }
    return current?.[id] ? true : undefined;
}

function updateChecked(id: string, checked: boolean) {
    const current = unref(selectedItems);
    if(current instanceof Map) {
        const next = new Map(current);
        next.set(id, checked);
        selectedItems.value = next;
        return;
    }
    const next: Record<string, boolean> = { ...(current ?? {}) };
    next[id] = checked;
    selectedItems.value = next;
}

function onToggle(id: string, event: Event) {
    const target = event.target as HTMLInputElement | null;
    updateChecked(id, !!target?.checked);
}

</script>
<template>
    <div class="form_row">
        <ul class="list-group width-100" :class="{ 'superfacile-checkbox-no-margin': !props.withMargin }">
            <li
                v-for="option in filteredOptions"
                :key="option.genid"
                class="list-group-item"
                :class="isGroupHeader(option) ? (option.classList ?? 'active') : []"
            >
                <div v-if="isGroupHeader(option)" class="superfacile-option-group">
                    <strong>{{ option.group }}</strong>
                </div>
                <div v-if="isOptionItem(option)" class="superfacile-option-item">
                    <label :for="option.genid" class="superfacile-checkbox-inline">
                        <input
                            :id="option.genid"
                            type="checkbox"
                            :name="_name"
                            :value="option.id"
                            :checked="isChecked(option.id)"
                            @change="(event) => onToggle(option.id, event)"
                        >
                        <span>
                            <strong v-if="props.withTitles && option.title && (option.title !== '--')">
                                {{ option.title }}
                                {{ option.text ? ' - ' : '' }}
                            </strong>
                            {{ option.text }}
                        </span>
                    </label>
                    <div class="superfacile-option-buttons">
                        <slot name="buttons" :option="option" />
                    </div>
                </div>
            </li>
        </ul>
        <div class="clearfix" />
    </div>
</template>
